import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Crown,
  Check,
  X,
  Sparkles,
  Calculator,
  Zap,
  Lock,
  RefreshCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  fetchPlans,
  fetchBillingMe,
  createCheckout,
  sandboxSimulateSuccess,
  launchPayHereCheckout,
  fmtLKR,
  featureMatrix,
} from "@/lib/billing";
import { useAuth } from "@/lib/auth";

/**
 * /pricing — Free, Basic, Premium with a Monthly/Annual toggle, ROI calculator,
 * and a sandbox-simulate button (visible only while running on the sandbox).
 */
const PricingPage = () => {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [billing, setBilling] = useState("monthly"); // monthly | annual
  const [loading, setLoading] = useState(false);
  const [brokerageInput, setBrokerageInput] = useState("11000");

  const load = async () => {
    try {
      const [d, m] = await Promise.all([fetchPlans(), fetchBillingMe()]);
      setData(d);
      setMe(m);
    } catch (e) {
      toast.error("Could not load plans");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const planFor = (tier) =>
    data?.plans?.find(
      (p) => p.tier === tier && p.billing_cycle === billing
    );

  const lifetimePlan = data?.plans?.find((p) => p.plan_id === "premium_lifetime");

  const checkout = async (planId) => {
    if (!user) {
      nav("/login");
      return;
    }
    try {
      setLoading(true);
      const ck = await createCheckout(planId);
      // Sandbox: show a "launch real checkout" button + simulate-success helper
      if (data?.mode === "sandbox") {
        toast.message("Sandbox checkout created", {
          description: `Order ${ck.order_id} \u2014 launching PayHere sandbox\u2026`,
        });
      }
      launchPayHereCheckout(ck);
      // Stash the order_id so the success page can verify it
      try {
        sessionStorage.setItem("cse_pending_order_id", ck.order_id);
      } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setLoading(false);
    }
  };

  const simulate = async (planId) => {
    try {
      setLoading(true);
      const ck = await createCheckout(planId);
      const res = await sandboxSimulateSuccess(ck.order_id);
      if (res?.status === "success") {
        toast.success("Sandbox payment activated!");
        await refresh();
        await load();
      } else {
        toast.error("Simulation failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally {
      setLoading(false);
    }
  };

  // ROI calc: brokerage paid last month vs Premium price
  const roi = useMemo(() => {
    const b = Number(brokerageInput) || 0;
    if (b <= 0) return null;
    const tradeVolume = b / 0.0121; // total LKR traded
    const premium = 2490;
    const breakEvenPct = (premium / tradeVolume) * 100;
    return {
      tradeVolume,
      premium,
      breakEvenPct,
    };
  }, [brokerageInput]);

  const currentTier = (me?.effective_tier || me?.tier || "free").toLowerCase();

  return (
    <div className="space-y-8" data-testid="pricing-page">
      {/* Header */}
      <header className="text-center max-w-3xl mx-auto space-y-3">
        <Badge
          variant="outline"
          className="border-amber-500/40 text-amber-600 dark:text-amber-400 mx-auto"
        >
          <Sparkles className="h-3 w-3 mr-1" /> Pricing for Sri Lankan investors
        </Badge>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
          Smarter CSE decisions
          <br />
          <span className="text-primary">for less than your weekly lunch</span>
        </h1>
        <p className="text-muted-foreground">
          All plans in LKR. No CGT (CSE listed shares). Brokerage 1.21% buy &
          sell. 14-day Premium trial included on every signup.
        </p>
        {me && (
          <p className="text-sm text-muted-foreground" data-testid="pricing-current-tier">
            You are currently on the <b className="capitalize">{currentTier}</b>{" "}
            tier
            {me.basic_expires_at &&
              currentTier === "basic" &&
              ` \u00b7 expires ${new Date(me.basic_expires_at).toLocaleDateString()}`}
            {me.premium_expires_at &&
              currentTier === "premium" &&
              ` \u00b7 expires ${new Date(me.premium_expires_at).toLocaleDateString()}`}
            {me.is_lifetime && " (lifetime)"}
          </p>
        )}
      </header>

      {/* Billing toggle */}
      <Tabs value={billing} onValueChange={setBilling} className="mx-auto w-fit">
        <TabsList data-testid="pricing-billing-toggle">
          <TabsTrigger value="monthly" data-testid="pricing-billing-monthly">
            Monthly
          </TabsTrigger>
          <TabsTrigger value="annual" data-testid="pricing-billing-annual">
            Annual <Badge className="ml-2" variant="secondary">Save 20%</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {/* FREE */}
        <PlanCard
          tier="free"
          highlight={false}
          currentTier={currentTier}
          priceLabel="Free"
          subLabel="No credit card required"
          features={featureMatrix.free.features}
          notIncluded={featureMatrix.free.notIncluded}
          ctaLabel={currentTier === "free" ? "Current plan" : "Continue free"}
          ctaDisabled={currentTier === "free"}
          onClick={() => nav("/dashboard")}
        />

        {/* BASIC */}
        <PlanCard
          tier="basic"
          highlight={false}
          currentTier={currentTier}
          priceLabel={
            billing === "monthly"
              ? `${fmtLKR(990)}/mo`
              : `${fmtLKR(9504)}/yr`
          }
          subLabel={
            billing === "annual"
              ? "Save Rs 2,376 vs monthly"
              : "Or Rs 9,504/yr (20% off)"
          }
          features={featureMatrix.basic.features}
          ctaLabel={currentTier === "basic" ? "Current plan" : "Upgrade to Basic"}
          ctaDisabled={currentTier === "basic" || loading}
          onClick={() =>
            checkout(billing === "monthly" ? "basic_monthly" : "basic_annual")
          }
          onSimulate={
            data?.mode === "sandbox"
              ? () =>
                  simulate(
                    billing === "monthly" ? "basic_monthly" : "basic_annual"
                  )
              : null
          }
        />

        {/* PREMIUM */}
        <PlanCard
          tier="premium"
          highlight
          currentTier={currentTier}
          priceLabel={
            billing === "monthly"
              ? `${fmtLKR(2490)}/mo`
              : `${fmtLKR(23904)}/yr`
          }
          subLabel={
            billing === "annual"
              ? "Save Rs 5,976 vs monthly"
              : "Or Rs 23,904/yr (20% off)"
          }
          features={featureMatrix.premium.features}
          ctaLabel={
            currentTier === "premium"
              ? me?.is_lifetime
                ? "Lifetime active"
                : "Current plan"
              : "Upgrade to Premium"
          }
          ctaDisabled={currentTier === "premium" || loading}
          onClick={() =>
            checkout(
              billing === "monthly" ? "premium_monthly" : "premium_annual"
            )
          }
          onSimulate={
            data?.mode === "sandbox"
              ? () =>
                  simulate(
                    billing === "monthly" ? "premium_monthly" : "premium_annual"
                  )
              : null
          }
        />
      </div>

      {/* Lifetime card */}
      {lifetimePlan && (
        <Card
          className="max-w-3xl mx-auto border-amber-500/40 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/10 relative overflow-hidden"
          data-testid="pricing-lifetime-card"
        >
          <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <Badge
                variant="outline"
                className="border-amber-500/40 text-amber-600 dark:text-amber-400 mb-2"
              >
                <Crown className="h-3 w-3 mr-1" /> Early adopter &middot; 200 seats
              </Badge>
              <CardTitle className="text-xl">Premium Lifetime</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Pay once. Use forever. Funds 12 months of development and gives
                you priority early access to every new feature.
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold tracking-tight">
                {fmtLKR(49000)}
              </div>
              <div className="text-xs text-muted-foreground">one-time</div>
              <Button
                className="mt-2"
                onClick={() => checkout("premium_lifetime")}
                disabled={loading}
                data-testid="pricing-lifetime-cta"
              >
                <Crown className="h-4 w-4" /> Lock in lifetime
              </Button>
              {data?.mode === "sandbox" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 ml-2"
                  onClick={() => simulate("premium_lifetime")}
                  data-testid="pricing-lifetime-simulate"
                >
                  <Zap className="h-3 w-3" /> Simulate
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>
      )}

      {/* ROI calculator */}
      <Card className="max-w-3xl mx-auto" data-testid="pricing-roi-calculator">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Will Premium pay for itself?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Enter the brokerage you paid last month. Premium costs Rs 2,490 —
            it pays for itself if it improves <b>just 0.5%</b> of one decision.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <label className="flex-1">
              <span className="text-xs text-muted-foreground">
                Last month&rsquo;s brokerage (LKR)
              </span>
              <Input
                type="number"
                value={brokerageInput}
                onChange={(e) => setBrokerageInput(e.target.value)}
                placeholder="11000"
                data-testid="pricing-roi-input"
                className="mt-1"
              />
            </label>
            <Button
              variant="outline"
              onClick={() => setBrokerageInput("11000")}
              data-testid="pricing-roi-reset"
            >
              <RefreshCcw className="h-4 w-4" /> Reset
            </Button>
          </div>
          {roi && (
            <div
              className="rounded-lg border border-border bg-muted/30 p-4 text-sm space-y-1"
              data-testid="pricing-roi-result"
            >
              <div>
                You traded approximately{" "}
                <b>{fmtLKR(roi.tradeVolume)}</b> last month (1.21% &rarr;{" "}
                {fmtLKR(roi.premium === 2490 ? Number(brokerageInput) : 0)}).
              </div>
              <div>
                Premium ({fmtLKR(roi.premium)}/mo) pays for itself if it
                improves just{" "}
                <b className="text-primary">
                  {roi.breakEvenPct.toFixed(2)}%
                </b>{" "}
                of one decision.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FAQ-style legal blurb */}
      <div className="max-w-3xl mx-auto text-xs text-muted-foreground space-y-1">
        <p>
          <b>Tax handling:</b> CSE listed shares are exempt from Capital Gains
          Tax in Sri Lanka. Dividends are subject to 10% Withholding Tax,
          deducted at source.
        </p>
        <p>
          <b>Payments:</b> Processed by PayHere (LKR cards, eZ Cash, FriMi).
          {data?.mode === "sandbox" && " Currently in SANDBOX mode."}
        </p>
      </div>
    </div>
  );
};

const PlanCard = ({
  tier,
  highlight,
  currentTier,
  priceLabel,
  subLabel,
  features,
  notIncluded = [],
  ctaLabel,
  ctaDisabled,
  onClick,
  onSimulate,
}) => {
  const meta = featureMatrix[tier];
  const isCurrent = currentTier === tier;
  return (
    <Card
      className={`relative flex flex-col ${
        highlight ? "border-primary shadow-lg" : ""
      }`}
      data-testid={`pricing-card-${tier}`}
    >
      {highlight && (
        <Badge
          className="absolute -top-3 left-1/2 -translate-x-1/2"
          data-testid={`pricing-card-${tier}-popular`}
        >
          <Sparkles className="h-3 w-3 mr-1" /> Most popular
        </Badge>
      )}
      <CardHeader className="space-y-2">
        <div className="flex items-center gap-2">
          {tier === "premium" ? (
            <Crown className="h-5 w-5 text-amber-500" />
          ) : tier === "basic" ? (
            <Zap className="h-5 w-5 text-primary" />
          ) : (
            <Lock className="h-5 w-5 text-muted-foreground" />
          )}
          <CardTitle className="capitalize">{meta.label}</CardTitle>
          {isCurrent && (
            <Badge variant="secondary" className="ml-auto">Current</Badge>
          )}
        </div>
        <div className="text-3xl font-bold tracking-tight" data-testid={`pricing-card-${tier}-price`}>
          {priceLabel}
        </div>
        <p className="text-xs text-muted-foreground">{subLabel}</p>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4">
        <ul className="space-y-2 text-sm">
          {features.map((f, i) => (
            <li
              key={i}
              className="flex gap-2"
              data-testid={`pricing-card-${tier}-feature-${i}`}
            >
              <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
          {notIncluded.map((f, i) => (
            <li
              key={`x-${i}`}
              className="flex gap-2 text-muted-foreground"
              data-testid={`pricing-card-${tier}-not-feature-${i}`}
            >
              <X className="h-4 w-4 text-muted-foreground/60 shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto pt-3 space-y-2">
          <Button
            onClick={onClick}
            disabled={ctaDisabled}
            variant={highlight ? "default" : "outline"}
            className="w-full"
            data-testid={`pricing-card-${tier}-cta`}
          >
            {ctaLabel}
          </Button>
          {onSimulate && !isCurrent && (
            <Button
              onClick={onSimulate}
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              data-testid={`pricing-card-${tier}-simulate`}
            >
              <Zap className="h-3 w-3" /> Simulate (sandbox)
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PricingPage;
